// Observable state fed by an AtmosWS instance. Wave 18 slice 1.
// Extended (2026-05-21 P1) to hold live agent roster + run timeline so
// the mini-app no longer falls back to mock JSON for those panes.

import {
  atmosWS,
  AtmosWS,
  type AtmosConv,
  type AtmosAgent,
  type AtmosRun,
  type AtmosFrame,
  type AtmosLinkState,
  type HistoryEntry,
} from "./atmosWS";

type VoidCb = () => void;

// Per-turn tool-call step. Mirrors the Mac `step` frame (kind="tool"
// today; future: kind="thought"|"search"|"file"). `done=true` is set
// when msg.end arrives so the strip can freeze its state.
export interface AtmosStep {
  ts: number;          // epoch seconds
  kind: string;        // "tool" | other future kinds
  text: string;        // short label (Bash, Read, Grep, ...)
  detail?: string;     // one-line arg preview (file path / command / query)
  done: boolean;
}

export interface AtmosStatus { state: string; word?: string }

export class AtmosStore {
  private _convs: AtmosConv[] = [];
  private _messages = new Map<number, string>();
  private _history = new Map<number, HistoryEntry[]>();
  private _agents: AtmosAgent[] = [];
  private _runs = new Map<string, AtmosRun>();
  private _steps = new Map<number, AtmosStep[]>();
  private _status = new Map<number, AtmosStatus>();
  private _link: AtmosLinkState = "down";
  private _authErr: string | null = null;
  private convSubs = new Set<VoidCb>();
  private msgSubs = new Map<number, Set<VoidCb>>();
  private msgEndSubs = new Map<number, Set<VoidCb>>();
  private historySubs = new Map<number, Set<VoidCb>>();
  private stepSubs = new Map<number, Set<VoidCb>>();
  private statusSubs = new Map<number, Set<VoidCb>>();
  private agentSubs = new Set<VoidCb>();
  private runSubs = new Set<VoidCb>();
  private linkSubs = new Set<VoidCb>();
  private authErrSubs = new Set<VoidCb>();

  constructor(ws: AtmosWS) {
    ws.onFrame((f) => this.handleFrame(f));
    ws.onLink((s) => {
      this._link = s;
      // Clear caches on full disconnect so a stale roster never bleeds
      // through a fresh pair to a different Mac. Also clear authErr
      // on connecting/paired so a stale "Auth failed" line doesn't
      // hang around across reconnect attempts.
      if (s === "down" || s === "error") {
        this._convs = [];
        this._messages.clear();
        this._history.clear();
        this._agents = [];
        this._runs.clear();
        this._steps.clear();
        this._status.clear();
        this.fire(this.convSubs);
        this.fire(this.agentSubs);
        this.fire(this.runSubs);
      }
      if (s === "connecting" || s === "paired") {
        if (this._authErr) {
          this._authErr = null;
          this.fire(this.authErrSubs);
        }
      }
      this.fire(this.linkSubs);
    });
  }

  convs(): AtmosConv[] { return this._convs.slice(); }
  messages(cid: number): string { return this._messages.get(cid) ?? ""; }
  history(cid: number): HistoryEntry[] { return this._history.get(cid) ?? []; }
  agents(): AtmosAgent[] { return this._agents.slice(); }
  runs(): AtmosRun[] { return Array.from(this._runs.values()); }
  steps(cid: number): AtmosStep[] { return (this._steps.get(cid) ?? []).slice(); }
  status(cid: number): AtmosStatus | null { return this._status.get(cid) ?? null; }

  /** Drop in-flight live state for `cid` — call when the user sends a
   *  fresh turn so chips/text from the previous turn don't bleed into
   *  the new bubble. The transcript history is unaffected. */
  clearTurn(cid: number): void {
    if (this._messages.has(cid)) { this._messages.delete(cid); this.fireMsg(cid); }
    if (this._steps.has(cid))    { this._steps.delete(cid);    this.fireSteps(cid); }
  }
  linkState(): AtmosLinkState { return this._link; }
  authErr(): string | null { return this._authErr; }

  onConvs(cb: VoidCb): () => void {
    this.convSubs.add(cb);
    return () => this.convSubs.delete(cb);
  }

  onMessages(cid: number, cb: VoidCb): () => void {
    let set = this.msgSubs.get(cid);
    if (!set) { set = new Set(); this.msgSubs.set(cid, set); }
    set.add(cb);
    return () => {
      const s = this.msgSubs.get(cid);
      if (s) { s.delete(cb); if (s.size === 0) this.msgSubs.delete(cid); }
    };
  }

  /** Fires exactly once when msg.end arrives for `cid` — gives the
   *  view a hook to do an end-of-turn pass (e.g. swap streamed
   *  textContent → rendered markdown HTML in one shot). */
  onMessageEnd(cid: number, cb: VoidCb): () => void {
    let set = this.msgEndSubs.get(cid);
    if (!set) { set = new Set(); this.msgEndSubs.set(cid, set); }
    set.add(cb);
    return () => {
      const s = this.msgEndSubs.get(cid);
      if (s) { s.delete(cb); if (s.size === 0) this.msgEndSubs.delete(cid); }
    };
  }

  onHistory(cid: number, cb: VoidCb): () => void {
    let set = this.historySubs.get(cid);
    if (!set) { set = new Set(); this.historySubs.set(cid, set); }
    set.add(cb);
    return () => {
      const s = this.historySubs.get(cid);
      if (s) { s.delete(cb); if (s.size === 0) this.historySubs.delete(cid); }
    };
  }

  onSteps(cid: number, cb: VoidCb): () => void {
    let set = this.stepSubs.get(cid);
    if (!set) { set = new Set(); this.stepSubs.set(cid, set); }
    set.add(cb);
    return () => {
      const s = this.stepSubs.get(cid);
      if (s) { s.delete(cb); if (s.size === 0) this.stepSubs.delete(cid); }
    };
  }

  onStatus(cid: number, cb: VoidCb): () => void {
    let set = this.statusSubs.get(cid);
    if (!set) { set = new Set(); this.statusSubs.set(cid, set); }
    set.add(cb);
    return () => {
      const s = this.statusSubs.get(cid);
      if (s) { s.delete(cb); if (s.size === 0) this.statusSubs.delete(cid); }
    };
  }

  onAgents(cb: VoidCb): () => void {
    this.agentSubs.add(cb);
    return () => this.agentSubs.delete(cb);
  }

  onRuns(cb: VoidCb): () => void {
    this.runSubs.add(cb);
    return () => this.runSubs.delete(cb);
  }

  onLink(cb: VoidCb): () => void {
    this.linkSubs.add(cb);
    return () => this.linkSubs.delete(cb);
  }

  onAuthErr(cb: VoidCb): () => void {
    this.authErrSubs.add(cb);
    return () => this.authErrSubs.delete(cb);
  }

  // --- internals ---

  private handleFrame(f: AtmosFrame): void {
    switch (f.type) {
      case "conv.list.r":
        this._convs = f.convs.slice();
        this.fire(this.convSubs);
        return;
      case "conv.update":
        this.applyConvUpdate(f);
        return;
      case "msg.delta":
        this._messages.set(f.cid, (this._messages.get(f.cid) ?? "") + f.text);
        this.fireMsg(f.cid);
        return;
      case "msg.end": {
        // Note: do NOT append "✓ done" into the message buffer — that
        // forced re-render and bled a literal marker into the rendered
        // markdown. The msg.end fan-out below tells the view to do its
        // own end-of-turn pass (e.g. markdown re-render).
        // Freeze any in-flight tool chips so they stop pulsing and
        // remain visible in the transcript as evidence of the run.
        const steps = this._steps.get(f.cid);
        if (steps && steps.length > 0) {
          for (const st of steps) st.done = true;
          this.fireSteps(f.cid);
        }
        this.fireMsgEnd(f.cid);
        return;
      }
      case "step": {
        const arr = this._steps.get(f.cid) ?? [];
        // De-dup back-to-back identical labels (Claude sometimes
        // re-emits tool_use on a retry — one chip per real call).
        const last = arr[arr.length - 1];
        if (!last || last.text !== f.text || last.kind !== f.kind || last.detail !== f.detail || last.done) {
          arr.push({
            ts: Math.floor(Date.now() / 1000),
            kind: f.kind,
            text: f.text,
            detail: f.detail,
            done: false,
          });
          if (arr.length > 32) arr.splice(0, arr.length - 32);
          this._steps.set(f.cid, arr);
          this.fireSteps(f.cid);
        }
        return;
      }
      case "status":
        this._status.set(f.cid, { state: f.state, word: f.word });
        this.fireStatus(f.cid);
        return;
      case "msg.history.r":
        this._history.set(f.cid, f.messages.slice());
        this.fireHistory(f.cid);
        return;
      case "agent.list.r":
        this._agents = f.agents.slice();
        this.fire(this.agentSubs);
        return;
      case "run.update":
        this._runs.set(f.run.id, f.run);
        this.fire(this.runSubs);
        return;
      case "auth.err":
        this._authErr = f.reason;
        this.fire(this.authErrSubs);
        return;
      default:
        return;
    }
  }

  private applyConvUpdate(u: Extract<AtmosFrame, { type: "conv.update" }>): void {
    const i = this._convs.findIndex((c) => c.cid === u.cid);
    if (u.action === "remove") {
      if (i >= 0) { this._convs.splice(i, 1); this.fire(this.convSubs); }
      return;
    }
    if (i < 0) {
      this._convs.push({
        cid: u.cid,
        name: u.name ?? "",
        kind: u.kind ?? "agent",
        state: u.state ?? "idle",
        origin: u.origin ?? "local",
      });
    } else {
      const cur = this._convs[i]!;
      this._convs[i] = {
        cid: cur.cid,
        name: u.name ?? cur.name,
        kind: u.kind ?? cur.kind,
        state: u.state ?? cur.state,
        origin: u.origin ?? cur.origin,
      };
    }
    this.fire(this.convSubs);
  }

  private fire(set: Set<VoidCb>): void {
    for (const cb of set) { try { cb(); } catch { /* swallow */ } }
  }

  private fireMsg(cid: number): void {
    const set = this.msgSubs.get(cid);
    if (set) this.fire(set);
  }

  private fireMsgEnd(cid: number): void {
    const set = this.msgEndSubs.get(cid);
    if (set) this.fire(set);
  }

  private fireHistory(cid: number): void {
    const set = this.historySubs.get(cid);
    if (set) this.fire(set);
  }

  private fireSteps(cid: number): void {
    const set = this.stepSubs.get(cid);
    if (set) this.fire(set);
  }

  private fireStatus(cid: number): void {
    const set = this.statusSubs.get(cid);
    if (set) this.fire(set);
  }
}

export const atmosStore = new AtmosStore(atmosWS);
